import React from 'react';
import { Box, Paper, Typography, Button, Stack, Divider, Alert } from '@mui/material';
import { Add } from '@mui/icons-material';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import QuestionsEditorHeader from '../campaigns/CampaignQuestionsEditor/edit/QuestionsEditorHeader';
import SortableGlobalPromptItem from './edit/SortableGlobalPromptItem';
import useGlobalPromptDrafts from './hooks/useGlobalPromptDrafts';
import type { GlobalPromptQuestion, SaveGlobalQuestion } from './types';

interface GlobalPromptQuestionsEditProps {
    initialQuestions: GlobalPromptQuestion[];
    onSave: (questions: SaveGlobalQuestion[]) => void | Promise<void>;
    onCancel: () => void;
    isSaving?: boolean;
}

const GlobalPromptQuestionsEdit: React.FC<GlobalPromptQuestionsEditProps> = ({ initialQuestions, onSave, onCancel, isSaving = false }) => {
    const {
        fields,
        fieldKeys,
        highlightedItemId,
        errors,
        sensors,
        isSortingDisabled,
        count,
        formMethods,
        handleAddQuestion,
        handleDelete,
        handleReorder,
        handleDragEnd,
        handleSave,
    } = useGlobalPromptDrafts(initialQuestions, onSave);

    const { control } = formMethods;

    return (
        <Paper sx={{ p: 2 }}>
            <QuestionsEditorHeader
                title={initialQuestions.length === 0 ? 'Add global questions' : 'Edit global questions'}
                questionsExist={count > 0}
                onCancel={onCancel}
                handleAddQuestion={handleAddQuestion}
            />
            <Stack
                spacing={1}
                component="form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSave();
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.target instanceof HTMLElement && e.target.tagName !== 'TEXTAREA') {
                        e.preventDefault();
                    }
                }}
                sx={{ mb: 1 }}
            >
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                    <SortableContext items={fieldKeys} strategy={verticalListSortingStrategy}>
                        {fields.map((field, index) => (
                            <SortableGlobalPromptItem
                                key={field.fieldKey}
                                fieldKey={field.fieldKey}
                                index={index}
                                control={control}
                                isFirst={index === 0}
                                isLast={index === count - 1}
                                isSortingDisabled={isSortingDisabled}
                                highlightedItemId={highlightedItemId}
                                onDelete={() => handleDelete(index)}
                                onReorder={handleReorder}
                            />
                        ))}
                    </SortableContext>
                </DndContext>

                <Box my={1}>
                    <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleAddQuestion}>
                        Add another question
                    </Button>
                </Box>

                <Box position="sticky" bottom={0} bgcolor="background.paper" sx={{ zIndex: 100, pb: 1 }}>
                    <Divider sx={{ mb: 1 }} />
                    {errors.questions !== undefined && (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            Some questions have errors. Please fix them before saving.
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            {count} {count === 1 ? 'question' : 'questions'}
                        </Typography>
                        <Button variant="contained" color="primary" size="small" type="submit" disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Stack>
        </Paper>
    );
};

export default GlobalPromptQuestionsEdit;
